import React from 'react';
import { Card, CardFooter, CardBody, Form, Row, Col } from 'reactstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { Link, useHistory, useParams, Prompt } from 'react-router-dom';
import { Button, Field, FormAlerts, useFetch } from '@cordata/buffet';
import PropTypes from 'prop-types';
import { snakeCase } from 'lodash';
import { parse } from 'date-fns';

import { authorizedInstance as axios } from '~/utils';

const validationSchema = Yup.object().shape({
  eventDate: Yup.date().required(),
  status: Yup.mixed().required(),
  facility: Yup.mixed().nullable(),
  dischargeDate: Yup.date().nullable(),
  comments: Yup.string().nullable(),
  note: Yup.string().nullable(),
});

const initialValues = {
  eventDate: undefined,
  status: undefined,
  facility: undefined,
  dischargeDate: undefined,
  comments: undefined,
  note: undefined,
};

const AdmissionsForm = ({ data, disabled }) => {
  const history = useHistory();
  const params = useParams();
  const [statusStatus, statuses] = useFetch('reference/reference-statuses', { params: { type: 'Admission' } });
  const [facilityStatus, facilities] = useFetch('reference/facilities');

  return (
    <Formik
      displayName="AdmissionsForm"
      validateOnBlur={false}
      validateOnChange={false}
      enableReinitialize={!!data}

      initialValues={{
        ...initialValues,
        ...data,
        ...(data && data.attributes),
        ...(data && data.relationships),
        eventDate: data?.attributes.eventDate && parse(data?.attributes.eventDate?.split('T')[0], 'yyyy-MM-dd', new Date()),
        dischargeDate: data?.attributes.dischargeDate && parse(data?.attributes.dischargeDate?.split('T')[0], 'yyyy-MM-dd', new Date()),
      }}

      validationSchema={validationSchema}

      onSubmit={(values, { setErrors, setSubmitting, resetForm }) => {
        const method = params.id === 'add' ? 'post' : 'put';
        const url = `admissions${params.id !== 'add' ? `/${params.id}` : ''}`;

        const p = {
          data: {
            status_uid: values.status?.id,
            facility_uid: values.facility?.id,
            ...Object.keys(values).reduce((a, m) => ({
              ...a,
              [snakeCase(m)]: values[m],
            }), {
              patient_uid: params.patient_id,
            }),
          },
        };

        axios[method](url, p)
          .then(() => {
            resetForm({ ...values });

            history.push(`/patients/${params.patient_id}/details`);
          })
          .catch(e => setErrors({ server: e.errors }))
          .then(() => setSubmitting(false));
      }}
    >
      {({
        handleSubmit,
        isSubmitting,
        submitCount,
        errors,
        dirty,
      }) => (
        <Card>
          <Form onSubmit={handleSubmit} noValidate>
            <CardBody>
              { submitCount >= 1 && errors && <FormAlerts errors={errors} /> }

              <Row form>
                <Col md>
                  <Field
                    name="eventDate"
                    label="Admission Date"
                    type="date"
                    disabled={disabled}
                    required
                  />
                </Col>
                <Col md>
                  <Field
                    name="status"
                    type="select"
                    options={statuses}
                    getOptionValue={o => o.id}
                    getOptionLabel={o => o.attributes.statusLabel}
                    loading={statusStatus === 'loading'}
                    disabled={disabled}
                    required
                  />
                </Col>
              </Row>
              <Field
                name="facility"
                type="select"
                options={facilities}
                getOptionValue={o => o.id}
                getOptionLabel={o => o.name || o.attributes.facilityName}
                loading={facilityStatus === 'loading'}
                disabled={disabled}
              />
              <Field
                name="dischargeDate"
                type="date"
                disabled={disabled}
              />
              <Field
                name="comments"
                type="textarea"
                disabled={disabled}
              />
              <Field
                name="note"
                type="textarea"
                disabled={disabled}
              />
            </CardBody>

            <CardFooter className="d-flex">
              <div className="ml-auto">
                <Button
                  type="cancel"
                  disabled={isSubmitting || disabled}
                  tag={Link}
                  to={`/patients/${params.patient_id}/details`}
                >
                  Cancel
                </Button>

                <Button type="submit" disabled={isSubmitting || disabled}>
                  {isSubmitting ? 'Working ...' : 'Save'}
                </Button>
              </div>
            </CardFooter>
          </Form>

          <Prompt
            when={dirty}
            message="You have unsaved changes. These will be lost if you leave this page."
          />
        </Card>
      )}
    </Formik>
  );
};

AdmissionsForm.propTypes = {
  data: PropTypes.shape().isRequired,
  disabled: PropTypes.bool,
};

AdmissionsForm.defaultProps = {
  disabled: false,
};

export default AdmissionsForm;
